import React, { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { apiPatch } from '~/util/api'
import { Button } from '~/lib/ui/button'
import { PaperClipIcon } from '@heroicons/react/24/outline'
import RichTextEditor from '~/lib/ui/rich-text-editor'
import { useMessaging } from './MessagingProvider'
import useTranslation from '../shared/TranslationProvider'
import ConfirmationModal from '~/components/shared/ConfirmationModal'
import { useToast } from '~/lib/ui/toast'

const ThreadReply = ({ setReplying, name }) => {
  const { t } = useTranslation('CoachMessageThread')
  const editorRef = useRef(null)
  const attachmentRef = useRef(null)
  const [fileName, setFileName] = useState(null)
  const { currentThreadId, queryClient, userIsCoach } = useMessaging()
  const { toast } = useToast()

  const cancelReply = () => {
    setReplying(false)
  }

  const patchReply = async (form) => {
    const patchPath = `${window.location.pathname}/${currentThreadId}.json`
    const res = await apiPatch(patchPath, form, true)
    return res
  }

  const sendReplyMutation = useMutation({
    mutationFn: (form) => patchReply(form),
    onSuccess: () => {
      cancelReply()
      queryClient.invalidateQueries(['messageThreads', currentThreadId])
      editorRef.current.setContents([])
      return true
    },
    onError: (error) => {
      const description =
        error?.response?.status === 422
          ? t('file_unsafe')
          : t('an_error_occurred')

      toast({
        description,
        variant: 'destructive',
      })
    },
  })

  const { isPending } = sendReplyMutation

  const [showConfirm, setShowConfirm] = useState(false)
  const actionsRef = useRef(null)

  useEffect(() => {
    actionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [])

  const sendReply = () => {
    const attachment = attachmentRef.current?.files[0]
    const body = editorRef.current.getSemanticHTML()

    const form = new FormData()
    form.append('coach_message_thread_reply[body]', body)
    form.append('coach_message_thread_reply[user_is_coach]', userIsCoach)
    if (attachment) {
      form.append('coach_message_thread_reply[attachment]', attachment)
    }

    sendReplyMutation.mutate(form)
  }

  const acceptFileTypes = ['.jpg', '.jpeg', '.png', '.pdf', '.docx', '.doc']

  const onFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      setFileName(file.name)
    }
  }

  return (
    <>
      <div className="flex min-h-40 shrink grow basis-64 flex-col pt-4">
        <RichTextEditor
          ref={editorRef}
          className="w-full flex-1 [--rte-min-h:4rem]"
        />
      </div>
      <div
        ref={actionsRef}
        className="mt-4 flex shrink-0 scroll-mb-6 flex-row flex-wrap justify-start gap-x-4 gap-y-2"
      >
        {typeof setReplying === 'function' && (
          <Button variant="outline" onClick={cancelReply} disabled={isPending}>
            {t('cancel')}
          </Button>
        )}
        <div>
          <Button
            variant="outline"
            onClick={() => {
              attachmentRef.current.click()
            }}
            disabled={isPending}
          >
            <PaperClipIcon className="mr-1 h-5" />
            {fileName ? fileName : t('attach_file')}
          </Button>
          <input
            ref={attachmentRef}
            type="file"
            id="attachment-uploader"
            accept={acceptFileTypes}
            style={{ display: 'none' }}
            onChange={onFileSelect}
            disabled={isPending}
          />
        </div>
        <Button
          variant="coachcta"
          onClick={userIsCoach ? () => setShowConfirm(true) : sendReply}
          disabled={isPending}
        >
          {t('reply')}
        </Button>
      </div>
      {userIsCoach && (
        <ConfirmationModal
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          onConfirm={() => {
            setShowConfirm(false)
            sendReply()
          }}
          title={t('confirm_send_title')}
          message={t('confirm_send_message', { name: name })}
        />
      )}
    </>
  )
}
export default ThreadReply
